<template>
  <v-btn
      class="my-2"
      :disabled="!isConnectedAndOnline"
      variant="tonal"
      :prepend-icon="mdiImageMultipleOutline"
      @click="openDialog"
  >
    {{ $t('global.info-modal.label.load-photos') }}
  </v-btn>

  <v-dialog
      v-model="dialog"
      max-width="500">
    <v-card>
      <v-card-title>
        {{ $t('global.info-modal.label.load-photos') }}
      </v-card-title>
      <v-card-text>
        <div v-if="loading" class="text-center my-4">
          <v-progress-circular indeterminate />
        </div>
        <div v-else-if="setInfo">
          <v-alert type="info" variant="tonal" class="mb-3">
            <div class="font-weight-bold">
              {{ formatDate(setInfo.createdAt) }}
            </div>
            <div>
              {{ setInfo.uploadedCount }} / {{ setInfo.expectedCount }}
              {{ $t('global.info-modal.label.photos-count') }}
            </div>
          </v-alert>
          <v-btn
              :disabled="loadingPhotos"
              :loading="loadingPhotos"
              color="primary"
              variant="tonal"
              block
              @click="dialogConfirm = true"
          >
            {{ $t('global.info-modal.label.download-photos') }}
          </v-btn>
          <div v-if="progressText" class="text-caption mt-2 text-center">
            {{ progressText }}
          </div>
        </div>
        <div v-else>
          <v-alert type="warning" variant="tonal">
            {{ $t('global.info-modal.label.no-photos-set') }}
          </v-alert>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="dialog = false">
          {{ $t('global.btn.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Confirmation dialog -->
  <v-dialog v-model="dialogConfirm" max-width="500px">
    <v-card>
      <v-card-text>
        <v-alert type="warning" :icon="mdiAlert">
          {{ $t('global.info-modal.label.photos-replace-warning') }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="dialogConfirm = false">
          {{ $t('global.btn.cancel') }}
        </v-btn>
        <v-btn color="red-darken-4" variant="text" @click="loadPhotos">
          {{ $t('global.info-modal.label.confirm-load-photos') }}
        </v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { mdiImageMultipleOutline, mdiAlert } from '@mdi/js';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import frontPicturesService from '@/services/frontPictures';
import localForageService from '@/services/localForage';
import { TABLE } from '@/constants';
import dateHandler from '@/utils/dateHandler';

const props = defineProps({
  isConnectedAndOnline: {
    type: Boolean,
    required: true
  }
});

const { t } = useI18n();

const dialog = ref(false);
const dialogConfirm = ref(false);
const loading = ref(false);
const loadingPhotos = ref(false);
const setInfo = ref(null);
const progressText = ref('');

const formatDate = (isoDate) => {
  if (!isoDate) return '';
  const unix = new Date(isoDate).getTime() / 1000;
  return dateHandler.convertUnixTimestampToStandardHuman(unix);
};

const openDialog = async () => {
  dialog.value = true;
  loading.value = true;
  try {
    setInfo.value = await frontPicturesService.getSetInfo();
  } catch (e) {
    console.error('Error fetching photo set info', e);
    setInfo.value = null;
  }
  loading.value = false;
};

const loadPhotos = async () => {
  dialogConfirm.value = false;
  loadingPhotos.value = true;
  progressText.value = '';

  try {
    // Get list of photos
    const photos = await frontPicturesService.listPhotos();

    // Clear existing pictures in localForage
    await localForageService.removeData(TABLE.QUESTION_PICTURES);

    // Download and store each photo
    for (let i = 0; i < photos.length; i++) {
      progressText.value = `${i + 1} / ${photos.length}`;
      try {
        const blob = await frontPicturesService.downloadPhoto(photos[i]);
        await localForageService.storeData(TABLE.QUESTION_PICTURES, photos[i], blob);
      } catch (e) {
        console.error(`Error downloading photo ${photos[i]}`, e);
      }
    }

    progressText.value = t('global.info-modal.label.photos-loaded-success');
  } catch (e) {
    console.error('Error loading photos', e);
    progressText.value = '';
  }

  loadingPhotos.value = false;
};
</script>

